import type { ReactNode } from 'react';

/**
 * CMS v2 has two general-purpose pill roles: the coral primary and a quiet `outline`
 * secondary. `ink` is the single exception — the primary on a coral section, where a coral
 * pill would sit on coral (see cmsTheme's `button`).
 *
 * Because an outline pill takes its color from the section it sits on, it comes in the two
 * tones the palette has ink for: dark ink on the light bases, yellow on the dark ones. A
 * section reads the right one off its combination (`t.outline`) rather than choosing.
 *
 * The v1 names (`green`, `yellow`, `ghost`, `ghostLight`) are still accepted and map onto
 * the roles above, so existing call sites keep working and pick up the new look.
 */
export type PillButtonVariant =
  | 'coral' | 'ink' | 'outline' | 'outlineLight'
  | 'green' | 'yellow' | 'ghost' | 'ghostLight';

interface PillButtonBase {
  children: ReactNode;
  variant?: PillButtonVariant;
  className?: string;
  icon?: ReactNode;
}

interface PillButtonAsButton extends PillButtonBase {
  as?: 'button';
  onClick?: () => void;
  href?: never;
}

interface PillButtonAsAnchor extends PillButtonBase {
  as: 'a';
  href?: string;
  onClick?: never;
}

export type PillButtonProps = PillButtonAsButton | PillButtonAsAnchor;

const VARIANTS: Record<PillButtonVariant, string> = {
  coral:        'bg-mf-coral text-white hover:bg-mf-coralImg',
  ink:          'bg-mf-ink text-mf-yellow hover:bg-mf-ink/90',
  outline:      'bg-transparent text-mf-black border border-mf-green/20 hover:bg-mf-green/5',
  outlineLight: 'bg-transparent text-mf-yellow border border-mf-yellow/20 hover:bg-mf-yellow/10',

  // v1 aliases — kept so call sites need no churn; each resolves to its v2 role.
  green:      'bg-mf-coral text-white hover:bg-mf-coralImg',
  yellow:     'bg-transparent text-mf-black border border-mf-green/20 hover:bg-mf-green/5',
  ghost:      'bg-transparent text-mf-black border border-mf-green/20 hover:bg-mf-green/5',
  ghostLight: 'bg-transparent text-mf-yellow border border-mf-yellow/20 hover:bg-mf-yellow/10',
};

export function PillButton({
  children, variant = 'coral', as = 'button', href, onClick, className = '', icon,
}: PillButtonProps) {
  const cls =
    `inline-flex items-center gap-2 px-5 md:px-6 py-2.5 md:py-3 rounded-full ` +
    `font-medium text-sm md:text-base transition-colors ${VARIANTS[variant] ?? ''} ${className}`;
  if (as === 'a') return <a href={href} className={cls}>{icon}{children}</a>;
  return <button type="button" onClick={onClick} className={cls}>{icon}{children}</button>;
}
